import React, { useState, useEffect } from 'react';
import { Row, Col } from 'react-bootstrap';
import { getUnlockedAchievements, getLockedAchievements, getStats } from '../utils/achievementUtils';

const Achievements = () => {
    const [unlockedAchievements, setUnlockedAchievements] = useState([]);
    const [lockedAchievements, setLockedAchievements] = useState([]);
    const [stats, setStats] = useState({});

    useEffect(() => {
        setUnlockedAchievements(getUnlockedAchievements());
        setLockedAchievements(getLockedAchievements());
        setStats(getStats());
    }, []);

    const getProgressForAchievement = (achievement) => {
        switch (achievement.id) {
            case 'quiz_master_10':
                return Math.min((stats.totalQuizzes / 10) * 100, 100);
            case 'quiz_master_50':
                return Math.min((stats.totalQuizzes / 50) * 100, 100);
            case 'high_scorer':
                return Math.min((stats.highScores / 10) * 100, 100);
            case 'reviewer':
                return Math.min((stats.reviewSessions / 10) * 100, 100);
            case 'noun_master':
                return Math.min((stats.categoriesCompleted?.nouns?.length / 3) * 100, 100);
            case 'verb_master':
                return Math.min((stats.categoriesCompleted?.verbs?.length / 3) * 100, 100);
            default:
                return 0;
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('ja-JP', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    return (
        <div className="modern-container">
            <div className="modern-section">
                {/* Header */}
                <div className="text-center fade-in-up" style={{ marginBottom: 'var(--spacing-2xl)' }}>
                    <h2 style={{ 
                        fontSize: '2.5rem',
                        background: 'var(--primary-gradient)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                        marginBottom: 'var(--spacing-md)'
                    }}>
                        🏆 実績
                    </h2>
                    <p style={{ 
                        color: 'var(--text-secondary)',
                        fontSize: '1.1rem',
                        maxWidth: '600px',
                        margin: '0 auto'
                    }}>
                        クイズの成果を確認して、新しい実績を解除しましょう
                    </p>
                </div>

                {/* Stats Overview */}
                <div className="modern-card fade-in-up" style={{ marginBottom: 'var(--spacing-2xl)' }}>
                    <div className="modern-card-header">
                        <h4 style={{ margin: 0, color: 'var(--text-primary)' }}>
                            📊 統計
                        </h4>
                    </div>
                    <div className="modern-card-body">
                        <Row>
                            <Col md={3} className="text-center">
                                <div style={{ 
                                    fontSize: '2rem',
                                    fontWeight: 'bold',
                                    color: 'var(--primary-color)',
                                    marginBottom: 'var(--spacing-sm)'
                                }}>
                                    {stats.totalQuizzes || 0}
                                </div>
                                <div style={{ color: 'var(--text-secondary)' }}>
                                    完了したクイズ
                                </div>
                            </Col>
                            <Col md={3} className="text-center">
                                <div style={{ 
                                    fontSize: '2rem',
                                    fontWeight: 'bold',
                                    color: 'var(--success-color)',
                                    marginBottom: 'var(--spacing-sm)'
                                }}>
                                    {stats.perfectScores || 0}
                                </div>
                                <div style={{ color: 'var(--text-secondary)' }}>
                                    満点獲得
                                </div>
                            </Col>
                            <Col md={3} className="text-center">
                                <div style={{ 
                                    fontSize: '2rem',
                                    fontWeight: 'bold',
                                    color: 'var(--warning-color)',
                                    marginBottom: 'var(--spacing-sm)'
                                }}>
                                    {stats.maxStreak || 0}
                                </div>
                                <div style={{ color: 'var(--text-secondary)' }}>
                                    最高連続正解
                                </div>
                            </Col>
                            <Col md={3} className="text-center">
                                <div style={{ 
                                    fontSize: '2rem',
                                    fontWeight: 'bold',
                                    color: 'var(--info-color)',
                                    marginBottom: 'var(--spacing-sm)'
                                }}>
                                    {unlockedAchievements.length}
                                </div>
                                <div style={{ color: 'var(--text-secondary)' }}>
                                    解除済み実績
                                </div>
                            </Col>
                        </Row>
                    </div>
                </div>

                {/* Unlocked Achievements */}
                {unlockedAchievements.length > 0 && (
                    <div className="modern-card fade-in-up" style={{ marginBottom: 'var(--spacing-2xl)' }}>
                        <div className="modern-card-header">
                            <h4 style={{ margin: 0, color: 'var(--text-primary)' }}>
                                ✨ 解除済み実績
                            </h4>
                        </div>
                        <div className="modern-card-body">
                            <Row>
                                {unlockedAchievements.map((achievement) => (
                                    <Col md={6} lg={4} key={achievement.id} className="mb-3">
                                        <div style={{
                                            padding: 'var(--spacing-lg)',
                                            background: 'var(--success-gradient)',
                                            borderRadius: 'var(--radius-lg)',
                                            color: 'white',
                                            textAlign: 'center',
                                            position: 'relative',
                                            overflow: 'hidden'
                                        }}>
                                            <div style={{
                                                position: 'absolute',
                                                top: '10px',
                                                right: '10px',
                                                fontSize: '1.2rem'
                                            }}>
                                                ✓
                                            </div>
                                            <div style={{ 
                                                fontSize: '3rem',
                                                marginBottom: 'var(--spacing-md)'
                                            }}>
                                                {achievement.icon}
                                            </div>
                                            <h5 style={{ 
                                                color: 'white',
                                                marginBottom: 'var(--spacing-sm)'
                                            }}>
                                                {achievement.name}
                                            </h5>
                                            <p style={{ 
                                                color: 'rgba(255,255,255,0.9)',
                                                marginBottom: 'var(--spacing-sm)',
                                                fontSize: '0.9rem'
                                            }}>
                                                {achievement.description}
                                            </p>
                                            <small style={{ 
                                                color: 'rgba(255,255,255,0.8)',
                                                fontSize: '0.8rem'
                                            }}>
                                                {formatDate(achievement.unlockedAt)}
                                            </small>
                                        </div>
                                    </Col>
                                ))}
                            </Row>
                        </div>
                    </div>
                )}

                {/* Locked Achievements */}
                <div className="modern-card fade-in-up">
                    <div className="modern-card-header">
                        <h4 style={{ margin: 0, color: 'var(--text-primary)' }}>
                            🔒 未解除実績
                        </h4>
                    </div>
                    <div className="modern-card-body">
                        <Row>
                            {lockedAchievements.map((achievement) => {
                                const progress = getProgressForAchievement(achievement);
                                return (
                                    <Col md={6} lg={4} key={achievement.id} className="mb-3">
                                        <div style={{
                                            padding: 'var(--spacing-lg)',
                                            background: 'var(--surface-primary)',
                                            border: '1px solid var(--border-color)',
                                            borderRadius: 'var(--radius-lg)',
                                            textAlign: 'center',
                                            position: 'relative',
                                            opacity: 0.7
                                        }}>
                                            <div style={{
                                                position: 'absolute',
                                                top: '10px',
                                                right: '10px',
                                                fontSize: '1.2rem',
                                                color: 'var(--text-muted)'
                                            }}>
                                                🔒
                                            </div>
                                            <div style={{ 
                                                fontSize: '3rem',
                                                marginBottom: 'var(--spacing-md)',
                                                filter: 'grayscale(100%)'
                                            }}>
                                                {achievement.icon}
                                            </div>
                                            <h5 style={{ 
                                                color: 'var(--text-secondary)',
                                                marginBottom: 'var(--spacing-sm)'
                                            }}>
                                                {achievement.name}
                                            </h5>
                                            <p style={{ 
                                                color: 'var(--text-muted)',
                                                marginBottom: 'var(--spacing-md)',
                                                fontSize: '0.9rem'
                                            }}>
                                                {achievement.description}
                                            </p>
                                            {progress > 0 && (
                                                <div style={{ marginTop: 'var(--spacing-md)' }}>
                                                    <div style={{
                                                        fontSize: '0.8rem',
                                                        color: 'var(--text-muted)',
                                                        marginBottom: 'var(--spacing-xs)'
                                                    }}>
                                                        進捗: {Math.round(progress)}%
                                                    </div>
                                                    <div style={{
                                                        height: '6px',
                                                        background: 'var(--border-color)',
                                                        borderRadius: '3px',
                                                        overflow: 'hidden'
                                                    }}>
                                                        <div style={{
                                                            width: `${progress}%`,
                                                            height: '100%',
                                                            background: 'var(--primary-gradient)',
                                                            transition: 'width 0.3s ease'
                                                        }} />
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </Col>
                                );
                            })}
                        </Row>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Achievements;